import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Eye, EyeOff, User } from 'lucide-react';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const result = await login(username, password);
        setLoading(false);
        if (result.success) {
            toast.success(`Selamat datang, ${result.data.name}!`);
            if (result.data.role === 'warga') navigate('/warga');
            else navigate('/dashboard');
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen flex" style={{ background: 'hsl(210 40% 98%)' }}>
            {/* Left panel */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
                 style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #0f766e 100%)' }}>
                <div className="absolute inset-0 opacity-10">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="absolute rounded-full border border-white/30"
                            style={{ width: `${120 + i * 80}px`, height: `${120 + i * 80}px`,
                                     top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                    ))}
                </div>
                <div className="relative z-10 flex flex-col justify-center px-16 py-12 text-white">
                    <div className="w-20 h-20 bg-white/10 p-2.5 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-sm border border-white/20 shadow-inner">
                        <img src="/logo_surabaya.svg" alt="Logo Pemkot Surabaya" className="w-full h-full object-contain drop-shadow" />
                    </div>
                    <h1 className="text-4xl font-extrabold mb-3 leading-tight tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        Portal Layanan RT 06
                    </h1>
                    <p className="text-blue-100 text-lg leading-relaxed mb-8">
                        Sistem Informasi dan Administrasi Warga RT 06 / RW 01 Menanggal, Kota Surabaya.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                        {[
                            { label: 'Data Warga', desc: 'KK & NIK terdigitalisasi' },
                            { label: 'Keuangan', desc: 'Iuran & laporan otomatis' },
                            { label: 'Pengumuman', desc: 'Kelola info warga' },
                            { label: 'Tamu & Pengaduan', desc: 'Wajib lapor & forum' },
                        ].map(item => (
                            <div key={item.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                <p className="font-bold text-sm">{item.label}</p>
                                <p className="text-blue-200 text-xs mt-1">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8">
                        <a href="/" className="text-white/60 hover:text-white text-sm flex items-center gap-2 transition-colors">
                            ← Kembali ke Halaman Utama
                        </a>
                    </div>
                </div>
            </div>

            {/* Right panel - Login form */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                <div className="lg:hidden flex items-center gap-3 mb-8">
                    <img src="/logo_surabaya.svg" alt="Logo Pemkot Surabaya" className="w-12 h-12 object-contain" />
                    <div>
                        <p className="font-bold text-slate-900 leading-tight">Sistem Informasi RT 06</p>
                        <p className="text-xs text-emerald-700 font-medium">Menanggal, Kota Surabaya</p>
                    </div>
                </div>

                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <h2 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            Masuk Sistem
                        </h2>
                        <p className="text-slate-500 mt-1">Masukkan username dan password Anda</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-sm font-semibold text-slate-700">
                                Username
                            </Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input id="username" type="text" value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    placeholder="Masukkan username" required
                                    className="h-11 pl-10 border-slate-200 focus:ring-primary"
                                    data-testid="login-username-input" />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                                Password
                            </Label>
                            <div className="relative">
                                <Input id="password" type={showPw ? 'text' : 'password'}
                                    value={password} onChange={e => setPassword(e.target.value)}
                                    placeholder="••••••••" required
                                    className="h-11 pr-10 border-slate-200 focus:ring-primary"
                                    data-testid="login-password-input" />
                                <button type="button" onClick={() => setShowPw(!showPw)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg"
                                data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full h-11 text-base font-semibold bg-primary hover:bg-primary/90"
                            disabled={loading} data-testid="login-submit-btn">
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Masuk...
                                </span>
                            ) : 'Masuk'}
                        </Button>
                    </form>

                    <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                        <p className="text-xs text-slate-500 font-medium">Default login:</p>
                        <p className="text-xs text-slate-600 mt-1">Username: <span className="font-mono font-bold">admin</span> &nbsp;|&nbsp; Password: <span className="font-mono font-bold">admin</span></p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-200">
                        <p className="text-center text-xs text-slate-400">
                            Sistem Informasi RT &copy; {new Date().getFullYear()} &mdash; Semua hak dilindungi
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
